import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, ArrowRight, ChevronLeft, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /auth/reset-password?token=… — collects a new password, sends it to
 * the backend which validates the token and returns a fresh session.
 */
const ResetPassword = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!token) {
    return (
      <div className="relative min-h-screen trubeaute-gradient">
        <Header />
        <section className="pt-32 pb-24 px-6 flex items-center justify-center min-h-[90vh]">
          <div className="max-w-md w-full bg-white border border-[#EADDD7] rounded-3xl p-10 text-center soft-shadow-lg">
            <h1 className="font-serif italic text-3xl text-[#2C2123] mb-3">
              {t("resetPassword.badLinkTitle")}
            </h1>
            <p className="text-[#5C4B51] mb-6">
              {t("resetPassword.badLinkBody")}
            </p>
            <Link
              to="/auth/forgot-password"
              className="inline-flex items-center gap-1.5 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3 text-sm transition-colors"
              data-testid="reset-request-new-btn"
            >
              {t("resetPassword.requestNewBtn")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    if (password.length < 8) {
      setError(t("resetPassword.errorLength"));
      return;
    }
    if (password !== confirm) {
      setError(t("resetPassword.errorMatch"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/password/reset`,
        { token, password },
        { withCredentials: true }
      );
      setUser(data);
      toast.success(t("resetPassword.successToast"));
      window.location.replace("/my-routines");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : t("resetPassword.errorGeneric")
      );
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-[#EADDD7] rounded-full pl-10 pr-10 py-3 text-[#2C2123] placeholder:text-[#8A7D82] focus:outline-none focus:ring-2 focus:ring-[#D5A8A4]/50 focus:border-[#D5A8A4] transition-all";

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <Header />
      <section
        className="pt-32 pb-24 px-6 flex items-center justify-center min-h-[90vh]"
        data-testid="reset-root"
      >
        <div className="max-w-md w-full">
          <div className="bg-white border border-[#EADDD7] rounded-3xl p-10 soft-shadow-lg">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center mb-6 soft-shadow">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
              {t("resetPassword.kicker")}
            </span>
            <h1 className="font-serif italic text-3xl sm:text-4xl text-[#2C2123] leading-tight mb-3">
              {t("resetPassword.headline")}
            </h1>
            <p className="text-[#5C4B51] mb-6 leading-relaxed">
              {t("resetPassword.intro")}
            </p>

            <form onSubmit={onSubmit} className="space-y-3">
              <div className="relative">
                <Lock className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("resetPassword.newPasswordPlaceholder")}
                  className={inputClass}
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A7D82] hover:text-[#2C2123] p-1"
                  aria-label={showPassword ? t("resetPassword.hidePasswordA11y") : t("resetPassword.showPasswordA11y")}
                  data-testid="reset-password-toggle"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <div className="relative">
                <Lock className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                  className={inputClass}
                  data-testid="reset-confirm-input"
                />
              </div>
              {error && (
                <div
                  className="text-sm text-[#9B3A3A] bg-[#FFF1F1] border border-[#F3C6C6] rounded-xl px-4 py-3"
                  data-testid="reset-error"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-base bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-60 shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5 transition-all duration-300"
                data-testid="reset-submit-btn"
              >
                {submitting ? (
                  <>
                    <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t("resetPassword.submitting")}
                  </>
                ) : (
                  <>
                    {t("resetPassword.submit")}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </div>
          <div className="mt-6 flex flex-col items-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors"
              data-testid="reset-back-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("resetPassword.backToSignIn")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResetPassword;
